import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { client } from '../services/client';
import { useUser } from '../hooks/useUser';
import {
  BattleChatMessage,
  BattleJoinRequest,
  BattleRole,
  BattleSession,
  BattleTokenResponse,
  BackingTrackSource,
} from '../interfaces/battleInterfaces';
import {
  createBattleSession,
  fetchBattleChat,
  fetchBattleSession,
  fetchBattleToken,
  fetchJoinRequests,
  pinParticipant,
  respondToJoinRequest,
  sendBattleChatMessage,
  submitJoinRequest,
  updateSessionStatus,
} from '../services/battleService';

// ── Context shape ─────────────────────────────────────────────────────────────

interface BattleContextValue {
  // Session state
  session: BattleSession | null;
  role: BattleRole | null;
  livekitToken: string | null;
  livekitUrl: string | null;
  isConnecting: boolean;
  error: string | null;

  // Chat
  chatMessages: BattleChatMessage[];
  sendChat: (message: string) => Promise<void>;

  // Join requests (host only)
  joinRequests: BattleJoinRequest[];
  approveRequest: (requestId: string) => Promise<void>;
  denyRequest: (requestId: string) => Promise<void>;

  // Host controls
  hostStartSession: (title: string) => Promise<void>;
  hostEndSession: () => Promise<void>;
  hostGoLive: () => Promise<void>;
  hostPinParticipant: (userId: string | null) => Promise<void>;

  // Participant / viewer entry
  viewerJoin: (sessionId: string) => Promise<void>;
  participantRequestJoin: (sessionId: string) => Promise<void>;

  // Backing track (host only)
  backingTrack: BackingTrackSource | null;
  setBackingTrack: (src: BackingTrackSource | null) => void;
  isPlayingTrack: boolean;
  playBackingTrack: () => void;
  stopBackingTrack: () => void;

  // Invite link
  inviteUrl: string | null;
}

const BattleContext = createContext<BattleContextValue | null>(null);

export const useBattle = (): BattleContextValue => {
  const ctx = useContext(BattleContext);
  if (!ctx) throw new Error('useBattle must be used inside BattleProvider');
  return ctx;
};

// ── Provider ──────────────────────────────────────────────────────────────────

export const BattleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useUser();

  const [session, setSession] = useState<BattleSession | null>(null);
  const [role, setRole] = useState<BattleRole | null>(null);
  const [livekitToken, setLivekitToken] = useState<string | null>(null);
  const [livekitUrl, setLivekitUrl] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [chatMessages, setChatMessages] = useState<BattleChatMessage[]>([]);
  const [joinRequests, setJoinRequests] = useState<BattleJoinRequest[]>([]);

  const [backingTrack, setBackingTrack] = useState<BackingTrackSource | null>(null);
  const [isPlayingTrack, setIsPlayingTrack] = useState(false);

  // Web Audio refs for backing track playback
  const audioCtxRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<AudioBufferSourceNode | AudioScheduledSourceNode | null>(null);
  const destNodeRef = useRef<MediaStreamAudioDestinationNode | null>(null);

  // Expose the backing track MediaStream so LiveKit hook can publish it
  const backingTrackStreamRef = useRef<MediaStream | null>(null);

  // ── Realtime subscriptions ──────────────────────────────────────────────────

  useEffect(() => {
    if (!session) return;

    // Subscribe to session row changes (status, pinned_participant)
    const sessionSub = client
      .channel('battle-session-' + session.id)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'battle_sessions',
          filter: 'id=eq.' + session.id,
        },
        (payload) => setSession((prev) => (prev ? { ...prev, ...payload.new } : prev))
      )
      .subscribe();

    // Subscribe to chat
    const chatSub = client
      .channel('battle-chat-' + session.id)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'battle_chat',
          filter: 'session_id=eq.' + session.id,
        },
        (payload) => setChatMessages((prev) => [...prev, payload.new as BattleChatMessage])
      )
      .subscribe();

    // Subscribe to join requests (host sees new ones arrive live)
    const requestSub = client
      .channel('battle-requests-' + session.id)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'battle_join_requests',
          filter: 'session_id=eq.' + session.id,
        },
        async () => {
          const updated = await fetchJoinRequests(session.id);
          setJoinRequests(updated);
        }
      )
      .subscribe();

    return () => {
      client.removeChannel(sessionSub);
      client.removeChannel(chatSub);
      client.removeChannel(requestSub);
    };
  }, [session?.id]);

  // ── Helpers ───────────────────────────────────────────────────────────────────

  const obtainToken = useCallback(
    async (sessionId: string, r: BattleRole): Promise<BattleTokenResponse> => {
      setIsConnecting(true);
      setError(null);
      try {
        const tokenData = await fetchBattleToken(sessionId, r);
        setLivekitToken(tokenData.token);
        setLivekitUrl(tokenData.livekitUrl);
        return tokenData;
      } catch (e: any) {
        setError(e.message);
        throw e;
      } finally {
        setIsConnecting(false);
      }
    },
    []
  );

  // ── Host actions ──────────────────────────────────────────────────────────────

  const hostStartSession = useCallback(
    async (title: string) => {
      if (!user) throw new Error('Not logged in');
      setError(null);
      const newSession = await createBattleSession(title, user.user_id);
      setSession(newSession);
      setRole('host');
      await obtainToken(newSession.id, 'host');
    },
    [user, obtainToken]
  );

  const hostGoLive = useCallback(async () => {
    if (!session) return;
    await updateSessionStatus(session.id, 'live');
  }, [session]);

  const hostEndSession = useCallback(async () => {
    if (!session) return;
    await updateSessionStatus(session.id, 'ended');
    stopBackingTrack();
    setSession(null);
    setLivekitToken(null);
    setRole(null);
    setChatMessages([]);
    setJoinRequests([]);
  }, [session]);

  const hostPinParticipant = useCallback(
    async (userId: string | null) => {
      if (!session) return;
      await pinParticipant(session.id, userId);
    },
    [session]
  );

  // ── Participant / viewer entry ─────────────────────────────────────────────────

  const viewerJoin = useCallback(
    async (sessionId: string) => {
      const s = await fetchBattleSession(sessionId);
      setSession(s);
      setRole('viewer');
      const [tokenData, chat] = await Promise.all([
        obtainToken(sessionId, 'viewer'),
        fetchBattleChat(sessionId),
      ]);
      setChatMessages(chat);
    },
    [obtainToken]
  );

  const participantRequestJoin = useCallback(
    async (sessionId: string) => {
      if (!user) throw new Error('Not logged in');
      await submitJoinRequest(sessionId, user.user_id, user.username, user.avatar_url ?? null);
      // Watch for approval then auto-upgrade to participant token
      const check = client
        .channel('my-request-' + sessionId)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'battle_join_requests',
            filter: 'session_id=eq.' + sessionId,
          },
          async (payload) => {
            if (payload.new.user_id === user.user_id && payload.new.status === 'approved') {
              client.removeChannel(check);
              const s = await fetchBattleSession(sessionId);
              setSession(s);
              setRole('participant');
              await obtainToken(sessionId, 'participant');
            }
          }
        )
        .subscribe();
    },
    [user, obtainToken]
  );

  // ── Join request management (host) ────────────────────────────────────────────

  const approveRequest = useCallback(async (requestId: string) => {
    await respondToJoinRequest(requestId, 'approved');
  }, []);

  const denyRequest = useCallback(async (requestId: string) => {
    await respondToJoinRequest(requestId, 'denied');
  }, []);

  // ── Chat ──────────────────────────────────────────────────────────────────────

  const sendChat = useCallback(
    async (message: string) => {
      if (!session || !user) return;
      await sendBattleChatMessage(
        session.id,
        user.user_id,
        user.username,
        user.avatar_url ?? null,
        message
      );
    },
    [session, user]
  );

  // ── Backing track (Web Audio → MediaStream → LiveKit) ─────────────────────────

  const stopBackingTrack = useCallback(() => {
    try {
      sourceNodeRef.current?.stop();
      // DO THIS THE RIGHT WAY !!!!!!!!!!!!!!!!!!!!!!!!!!!!!
    } catch (error) {
      console.log('ERROR IN BATTLE CHAT');
    }
    sourceNodeRef.current = null;
    audioCtxRef.current?.close();
    audioCtxRef.current = null;
    destNodeRef.current = null;
    backingTrackStreamRef.current = null;
    setIsPlayingTrack(false);
  }, []);

  const playBackingTrack = useCallback(async () => {
    if (!backingTrack) return;
    stopBackingTrack();

    const ctx = new AudioContext();
    audioCtxRef.current = ctx;
    const dest = ctx.createMediaStreamDestination();
    destNodeRef.current = dest;
    backingTrackStreamRef.current = dest.stream;

    let buffer: AudioBuffer;

    if (backingTrack.type === 'file') {
      const arrayBuffer = await backingTrack.file.arrayBuffer();
      buffer = await ctx.decodeAudioData(arrayBuffer);
    } else {
      // Library URL — fetch and decode
      const res = await fetch(backingTrack.url);
      const arrayBuffer = await res.arrayBuffer();
      buffer = await ctx.decodeAudioData(arrayBuffer);
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(dest);
    source.connect(ctx.destination); // host hears it locally too
    source.start(0);
    sourceNodeRef.current = source;
    setIsPlayingTrack(true);

    source.onended = () => setIsPlayingTrack(false);
  }, [backingTrack, stopBackingTrack]);

  // ── Invite URL ─────────────────────────────────────────────────────────────────

  const inviteUrl = session ? `${window.location.origin}/battle/${session.id}` : null;

  // ── Context value ─────────────────────────────────────────────────────────────

  const value: BattleContextValue = {
    session,
    role,
    livekitToken,
    livekitUrl,
    isConnecting,
    error,
    chatMessages,
    sendChat,
    joinRequests,
    approveRequest,
    denyRequest,
    hostStartSession,
    hostEndSession,
    hostGoLive,
    hostPinParticipant,
    viewerJoin,
    participantRequestJoin,
    backingTrack,
    setBackingTrack,
    isPlayingTrack,
    playBackingTrack,
    stopBackingTrack,
    inviteUrl,
  };

  return <BattleContext.Provider value={value}>{children}</BattleContext.Provider>;
};
